import * as React from 'react';
import { siteConfig } from '@sighthost/config';
import { cn } from '../lib/cn';

/**
 * BrandLogo: the only way to put the SightHost logo in a React surface.
 *
 * Renders the official pack artwork byte for byte. Never recolour it with a CSS
 * filter: the white and black versions are separate official files. Files are
 * copied into each app by scripts/sync-brand-assets.mjs.
 *
 * Variant
 *   wordmark  Mark and name. Use below about 200px wide.
 *   tagline   Adds HOSTING SOUTH AFRICA. Use at 200px wide and above, where the
 *             tagline stays legible.
 *   icon      The mark alone.
 *
 * Tone
 *   colour  Light backgrounds only. Teal on #212121 is 2.80:1.
 *   white   Dark backgrounds and photographs.
 *   black   Single-colour print and light backgrounds.
 *   auto    Portal only: colour in the light theme, white in the dark theme,
 *           switched by .brand-logo-* in portal/src/globals.css.
 *
 * Size it with a height class in `className`; width follows the artwork. Each
 * file carries about 3% clear space on every side.
 */
export type BrandLogoVariant = 'wordmark' | 'tagline' | 'icon';
export type BrandLogoTone = 'colour' | 'white' | 'black' | 'auto';

export interface BrandLogoProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'width' | 'height'> {
  variant?: BrandLogoVariant;
  tone?: BrandLogoTone;
}

const DIMENSIONS: Record<BrandLogoVariant, { width: number; height: number }> = {
  wordmark: { width: 600, height: 209 },
  tagline: { width: 600, height: 209 },
  icon: { width: 256, height: 256 },
};

const src = (variant: BrandLogoVariant, tone: Exclude<BrandLogoTone, 'auto'>) =>
  variant === 'icon' ? `/brand/icon-${tone}.png` : `/brand/logo-${variant}-${tone}.png`;

export function BrandLogo({
  variant = 'wordmark',
  tone = 'colour',
  alt = siteConfig.name,
  className,
  decoding = 'async',
  ...props
}: BrandLogoProps) {
  const size = DIMENSIONS[variant];
  const classes = cn('w-auto shrink-0', className);

  if (tone === 'auto') {
    return (
      <>
        <img
          {...props}
          {...size}
          src={src(variant, 'colour')}
          alt={alt}
          decoding={decoding}
          className={cn(classes, 'brand-logo-light')}
        />
        <img
          {...props}
          {...size}
          src={src(variant, 'white')}
          alt={alt}
          decoding={decoding}
          className={cn(classes, 'brand-logo-dark')}
        />
      </>
    );
  }

  return (
    <img {...props} {...size} src={src(variant, tone)} alt={alt} decoding={decoding} className={classes} />
  );
}
